import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { LicensingTier, ProductFeature } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, RemoteData, tapFailure, unwrapData } from '@ftr/foundation'
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store'
import { memoize } from 'lodash-es'
import { EMPTY, Observable, catchError, of, tap } from 'rxjs'
import {
  ConfigurePersonalizationService,
  CoreConfigurationService,
  GetCourtSystemService,
  PersonalizationConfiguration,
} from '../services'
import {
  FetchCourtSystemAction,
  FetchCourtSystemPersonalizationAction,
  FetchCourtSystemProductFeaturesAction,
  FetchCourtSystemPublicConfiguration,
} from './court-systems.actions'
import { CourtSystemState, CourtSystemsStateModel } from './court-systems.model'

export function defaultCourtSystemState(): CourtSystemsStateModel {
  return {}
}

/**
 * Intention:
 * To prevent duplicate requests and reduce boilerplate in components.
 * Currently we access courtSystemId and pass it to components, then call courtSystemService.get(id).
 * CourtSystemBreadcrumbResolver has already done the work to fetch the court system before the page has loaded,
 * so there is no reason to request it again in the component.
 * CourtSystemService could implement its own caching, but that doesn't reduce the boilerplate.
 *
 * This could become a BreadcrumbEntityState if we want, where we could store things like locations or user group names,
 * and getEntityById(UserGroup, id), updating the state model to be [UserGroup.name]: {[id]: UserGroup} for example.
 *
 * Suggestion:
 * Dispatch an action in the breadcrumb resolver to store the response, and access that in the Page component
 * e.g. see CourtSystemPage
 * NOTE: This does mean that you will only be able to use the courtSystemById selector,
 * if you are using it on a page that the court system breadcrumb resolver is nested under.
 */
@State<CourtSystemsStateModel>({
  name: 'courtSystemsState',
  defaults: defaultCourtSystemState(),
})
@Injectable()
export class CourtSystemsState {
  constructor(
    private readonly getCourtSystemService: GetCourtSystemService,
    private readonly personalizationService: ConfigurePersonalizationService,
    private readonly coreConfigurationService: CoreConfigurationService,
    private readonly router: Router,
  ) {}

  @Selector()
  static state(state: CourtSystemsStateModel): CourtSystemsStateModel {
    return state
  }

  /**
   * Relies on CourtSystemBreadcrumbResolver being defined in a parent route, to the component
   * at the route calling this selector.
   * @param courtSystemId
   */
  static courtSystemById = memoize(
    (courtSystemId: Uuid): ((state: CourtSystemsStateModel) => CourtSystem | undefined) => {
      return createSelector([CourtSystemsState], (state: CourtSystemsStateModel) => state[courtSystemId]?.details)
    },
  )

  /**
   * Relies on CourtSystemBreadcrumbResolver being defined in a parent route, to the component
   * at the route calling this selector.
   * @param courtSystemId
   */
  static licensingTier = memoize(
    (courtSystemId: Uuid): ((state: CourtSystemsStateModel) => LicensingTier | undefined) => {
      return createSelector(
        [CourtSystemsState],
        (state: CourtSystemsStateModel) => state[courtSystemId]?.details?.licensingTier,
      )
    },
  )

  static hasProductFeature = memoize(
    (courtSystemId: Uuid, productFeature: ProductFeature): ((state: CourtSystemsStateModel) => RemoteData<boolean>) => {
      return createSelector(
        [CourtSystemsState],
        (state: CourtSystemsStateModel) =>
          state[courtSystemId]?.productFeatures.map(features => !!features?.includes(productFeature)) ||
          RemoteData.notAsked(),
      )
    },
    (courtSystemId, productFeature) => `${courtSystemId}:${productFeature}`,
  )

  static publicConfiguration = memoize(
    (courtSystemId: Uuid): ((state: CourtSystemsStateModel) => RemoteData<PublicConfiguration>) => {
      return createSelector(
        [CourtSystemsState],
        (state: CourtSystemsStateModel) => state[courtSystemId]?.publicConfig ?? RemoteData.notAsked(),
      )
    },
  )

  /**
   * Relies on CourtSystemBreadcrumbResolver being defined in a parent route, to the component
   * at the route calling this selector.
   * @param state
   */
  @Selector()
  static courtSystemPersonalization(
    state: CourtSystemsStateModel,
  ): (courtSystemId: Uuid) => RemoteData<PersonalizationConfiguration | undefined> {
    return (courtSystemId: Uuid): RemoteData<PersonalizationConfiguration | undefined> => {
      return state[courtSystemId]?.personalization ?? RemoteData.notAsked()
    }
  }

  static readonly courtSystemProductFeatures = memoize(
    (courtSystemId: Uuid): ((state: CourtSystemsStateModel) => ProductFeature[]) => {
      return createSelector(
        [CourtSystemsState],
        (state: CourtSystemsStateModel) => state[courtSystemId]?.productFeatures.data ?? [],
      )
    },
  )

  @Selector()
  static isProductFeatureAllowed(
    state: CourtSystemsStateModel,
  ): (courtSystemId: Uuid, productFeature: ProductFeature) => RemoteData<boolean> {
    return (courtSystemId: Uuid, productFeature: ProductFeature): RemoteData<boolean> => {
      if (!state[courtSystemId]?.productFeatures) {
        return RemoteData.notAsked()
      }
      if (state[courtSystemId]?.productFeatures.isLoading()) {
        return RemoteData.loading()
      }
      const productFeatures = state[courtSystemId]?.productFeatures.data
      return RemoteData.success(productFeatures?.includes(productFeature) ?? false)
    }
  }

  @Action(FetchCourtSystemAction, { cancelUncompleted: true })
  fetchCourtSystem(
    { getState, patchState }: StateContext<CourtSystemsStateModel>,
    { courtSystemId, bypassCache }: FetchCourtSystemAction,
  ): Observable<CourtSystem> {
    const courtSystemById = getState()[courtSystemId]?.details
    if (courtSystemById && !bypassCache) {
      return of(courtSystemById)
    }
    // We prevent the breadcrumb resolver from returning while the request is in RemoteData.Loading
    return this.getCourtSystemService.getCourtSystem(courtSystemId).pipe(
      tapFailure(_ => this.router.navigate(['/'])),
      unwrapData(),
      catchError(_ => EMPTY),
      tap(courtSystem => {
        patchState({
          [courtSystem.id]: {
            ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
            details: courtSystem,
          },
        })
      }),
    )
  }

  @Action(FetchCourtSystemPersonalizationAction)
  fetchCourtSystemPersonalization(
    { getState, patchState }: StateContext<CourtSystemsStateModel>,
    { courtSystemId, bypassCache }: FetchCourtSystemPersonalizationAction,
  ): ApiResult<PersonalizationConfiguration | undefined> {
    const personalizationData = getState()[courtSystemId]?.personalization
    if (personalizationData && (personalizationData.isSuccess() || personalizationData.isLoading()) && !bypassCache) {
      return of(personalizationData)
    }

    patchState({
      [courtSystemId]: {
        ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
        personalization: RemoteData.loading(),
      },
    })

    return this.personalizationService.getPersonalizationConfiguration(courtSystemId).pipe(
      tap(rd =>
        patchState({
          [courtSystemId]: {
            ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
            personalization: rd,
          },
        }),
      ),
    )
  }

  @Action(FetchCourtSystemProductFeaturesAction)
  fetchCourtSystemProductFeatures(
    { getState, patchState }: StateContext<CourtSystemsStateModel>,
    { courtSystemId, bypassCache }: FetchCourtSystemProductFeaturesAction,
  ): ApiResult<ProductFeature[] | undefined> {
    const productFeatures = getState()[courtSystemId]?.productFeatures
    if (productFeatures && (productFeatures.isSuccess() || productFeatures.isLoading()) && !bypassCache) {
      return of(productFeatures)
    }

    patchState({
      [courtSystemId]: {
        ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
        productFeatures: RemoteData.loading(),
      },
    })

    return this.getCourtSystemService.getCourtSystemProductFeatures(courtSystemId).pipe(
      tap(productFeaturesRemote =>
        patchState({
          [courtSystemId]: {
            ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
            productFeatures: productFeaturesRemote,
          },
        }),
      ),
    )
  }

  @Action(FetchCourtSystemPublicConfiguration)
  fetchCourtSystemPublicConfiguration(
    { getState, patchState }: StateContext<CourtSystemsStateModel>,
    { courtSystemId, bypassCache }: FetchCourtSystemProductFeaturesAction,
  ): ApiResult<PublicConfiguration> {
    const publicConfig = getState()[courtSystemId]?.publicConfig
    if (publicConfig && (publicConfig.isSuccess() || publicConfig.isLoading()) && !bypassCache) {
      return of(publicConfig)
    }

    patchState({
      [courtSystemId]: {
        ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
        publicConfig: RemoteData.loading(),
      },
    })

    return this.coreConfigurationService.getByCourtSystem(courtSystemId).pipe(
      tap(publicConfigRemote =>
        patchState({
          [courtSystemId]: {
            ...this.getCourtSystemStateOrDefault(getState, courtSystemId),
            publicConfig: publicConfigRemote,
          },
        }),
      ),
    )
  }

  private getCourtSystemStateOrDefault(getState: () => CourtSystemsStateModel, courtSystemId: Uuid): CourtSystemState {
    const courtSystemState = getState()[courtSystemId]
    return (
      courtSystemState ?? {
        details: undefined,
        personalization: RemoteData.notAsked(),
        productFeatures: RemoteData.notAsked(),
        publicConfig: RemoteData.notAsked(),
      }
    )
  }
}
